import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { formatCurrency } from "@/lib/currencyUtils";
import { format } from "date-fns";
import { ScrollArea } from "@/components/ui/scroll-area";

type PaymentMode = "record" | "add" | "edit";

interface EditQuotePaymentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  quoteId: string;
  quoteNumber: string;
  totalAmount: number;
  currentPaymentStatus: string | null;
  currentPaymentAmount: number | null;
  currentPaymentReference: string | null;
  leadId: string;
  leadStatus: string;
  currency: string;
  mode?: PaymentMode;
}

export const EditQuotePaymentDialog = ({
  open,
  onOpenChange,
  quoteId,
  quoteNumber,
  totalAmount,
  currentPaymentStatus,
  currentPaymentAmount,
  currentPaymentReference,
  leadId,
  leadStatus,
  currency,
  mode = "record",
}: EditQuotePaymentDialogProps) => {
  const queryClient = useQueryClient();
  const isEdit = mode === "edit";
  const isAdd = mode === "add";
  const isRecord = mode === "record";

  const [paymentAmount, setPaymentAmount] = useState("");
  const [paymentReference, setPaymentReference] = useState("");
  const [paymentStatus, setPaymentStatus] = useState("paid");
  const [correctionReason, setCorrectionReason] = useState("");
  const [paymentNotes, setPaymentNotes] = useState("");

  // Fetch existing payments for this quote
  const { data: existingPayments } = useQuery({
    queryKey: ["quote-payments", quoteId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("quote_payments")
        .select("*")
        .eq("quotation_id", quoteId)
        .order("payment_date", { ascending: true });
      if (error) throw error;
      return data || [];
    },
    enabled: open,
  });

  const totalPaidSoFar = existingPayments?.reduce((sum, p) => sum + Number(p.amount), 0) ?? (currentPaymentAmount || 0);
  const remainingBalance = totalAmount - totalPaidSoFar;

  useEffect(() => {
    if (open) {
      if (isEdit) {
        // Edit mode: pre-fill with current values for correction
        setPaymentAmount(currentPaymentAmount?.toString() || totalAmount.toString());
        setPaymentReference(currentPaymentReference || "");
        setPaymentStatus(currentPaymentStatus === "partial" ? "partial" : "paid");
      } else if (isAdd) {
        // Add mode: empty amount for new installment
        setPaymentAmount("");
        setPaymentReference("");
        setPaymentStatus("partial");
      } else {
        // Record mode: first payment, default to full amount
        setPaymentAmount(totalAmount.toString());
        setPaymentReference("");
        setPaymentStatus("paid");
      }
      setCorrectionReason("");
      setPaymentNotes("");
    }
  }, [open, mode, currentPaymentAmount, currentPaymentReference, currentPaymentStatus, totalAmount]);

  // Auto-set status based on amount for record/add modes
  const handleAmountChange = (val: string) => {
    setPaymentAmount(val);
    if (isEdit) return; // Don't auto-change status in edit mode
    const num = parseFloat(val);
    if (!isNaN(num)) {
      const newTotal = isAdd ? totalPaidSoFar + num : num;
      if (newTotal >= totalAmount) {
        setPaymentStatus("paid");
      } else if (newTotal > 0) {
        setPaymentStatus("partial");
      } else {
        setPaymentStatus("unpaid");
      }
    }
  };

  const displayedRemaining = isAdd
    ? remainingBalance - (parseFloat(paymentAmount) || 0)
    : totalAmount - (parseFloat(paymentAmount) || 0);

  const mutation = useMutation({
    mutationFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const amount = parseFloat(paymentAmount);
      if (isNaN(amount) || amount < 0) throw new Error("Invalid amount");
      if (isEdit && !correctionReason.trim()) throw new Error("Correction reason is required");

      if (isEdit) {
        // EDIT MODE: Record correction and update quotation
        const { error: correctionError } = await supabase
          .from("quote_payment_corrections")
          .insert({
            quotation_id: quoteId,
            corrected_by: user.id,
            old_payment_status: currentPaymentStatus,
            new_payment_status: paymentStatus,
            old_payment_amount: currentPaymentAmount,
            new_payment_amount: amount,
            old_payment_reference: currentPaymentReference,
            new_payment_reference: paymentReference || null,
            correction_reason: correctionReason.trim(),
          });
        if (correctionError) throw correctionError;

        const updateData: Record<string, any> = {
          payment_amount: amount,
          payment_status: paymentStatus,
          payment_reference: paymentReference || null,
        };
        if (paymentStatus === "paid" || paymentStatus === "partial") {
          updateData.payment_received_at = new Date().toISOString();
        }
        if (paymentStatus === "paid") {
          updateData.status = "accepted";
        }

        const { error: quoteError } = await supabase
          .from("quotations")
          .update(updateData)
          .eq("id", quoteId);
        if (quoteError) throw quoteError;

        // Activity log
        await supabase.from("activity_log").insert({
          user_id: user.id,
          lead_id: leadId,
          action: "QUOTE_PAYMENT_CORRECTED",
          details: `Corrected payment on ${quoteNumber}: ${formatCurrency(currentPaymentAmount || 0, currency)} → ${formatCurrency(amount, currency)} (${paymentStatus}). Reason: ${correctionReason.trim()}`,
        });
      } else {
        // RECORD or ADD MODE: Insert a payment record into the ledger
        const { error: paymentError } = await supabase
          .from("quote_payments")
          .insert({
            quotation_id: quoteId,
            amount,
            payment_reference: paymentReference || null,
            payment_method: "manual",
            recorded_by: user.id,
            notes: paymentNotes || null,
          });
        if (paymentError) throw paymentError;

        // Calculate new total paid
        const newTotalPaid = isAdd ? totalPaidSoFar + amount : amount;
        const newStatus = newTotalPaid >= totalAmount ? "paid" : newTotalPaid > 0 ? "partial" : "unpaid";

        const updateData: Record<string, any> = {
          payment_amount: newTotalPaid,
          payment_status: newStatus,
          payment_reference: paymentReference || currentPaymentReference || null,
        };
        if (newStatus === "paid" || newStatus === "partial") {
          updateData.payment_received_at = new Date().toISOString();
        }
        if (newStatus === "paid") {
          updateData.status = "accepted";
        }

        const { error: quoteError } = await supabase
          .from("quotations")
          .update(updateData)
          .eq("id", quoteId);
        if (quoteError) throw quoteError;

        // Activity log
        const action = isAdd ? "QUOTE_PAYMENT_ADDED" : "QUOTE_PAYMENT_RECORDED";
        const details = isAdd
          ? `Added payment of ${formatCurrency(amount, currency)} to ${quoteNumber}. Total paid: ${formatCurrency(newTotalPaid, currency)} (${newStatus})`
          : `Recorded payment on ${quoteNumber}: ${formatCurrency(amount, currency)} (${newStatus})`;

        await supabase.from("activity_log").insert({
          user_id: user.id,
          lead_id: leadId,
          action,
          details,
        });
      }

      // If fully paid, move lead to executing
      const effectiveStatus = isEdit ? paymentStatus : (isAdd ? (totalPaidSoFar + parseFloat(paymentAmount) >= totalAmount ? "paid" : "partial") : paymentStatus);
      if (effectiveStatus === "paid") {
        const { error: leadError } = await supabase
          .from("leads")
          .update({ status: "executing" })
          .eq("id", leadId);
        if (leadError) throw leadError;

        const { data: { user: currentUser } } = await supabase.auth.getUser();
        await supabase.from("lead_changes").insert({
          lead_id: leadId,
          user_id: currentUser?.id || null,
          field_changed: "status",
          old_value: leadStatus,
          new_value: "executing",
          change_source: isEdit ? "payment_correction" : "manual_payment_confirmation",
        });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["quotations", leadId] });
      queryClient.invalidateQueries({ queryKey: ["quote-payments", quoteId] });
      queryClient.invalidateQueries({ queryKey: ["lead", leadId] });
      queryClient.invalidateQueries({ queryKey: ["leads"] });
      const msg = isEdit ? "Payment corrected successfully" : isAdd ? "Payment added successfully" : "Payment recorded successfully";
      toast.success(msg);
      onOpenChange(false);
    },
    onError: (error) => {
      console.error("Payment update error:", error);
      toast.error(error.message || "Failed to update payment");
    },
  });

  const canSubmit =
    paymentAmount &&
    parseFloat(paymentAmount) > 0 &&
    (!isEdit || correctionReason.trim().length > 0);

  const dialogTitle = isEdit ? "Edit Payment" : isAdd ? "Add Payment" : "Record Payment";
  const dialogDescription = isEdit
    ? `Amend payment details for ${quoteNumber}`
    : isAdd
    ? `Add another payment installment for ${quoteNumber}`
    : `Record a payment for ${quoteNumber}`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{dialogTitle}</DialogTitle>
          <DialogDescription>{dialogDescription}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="bg-muted/50 rounded-lg p-3 text-sm space-y-1">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Quote Total:</span>
              <span className="font-semibold">{formatCurrency(totalAmount, currency)}</span>
            </div>
            {(isAdd || (isRecord && existingPayments && existingPayments.length > 0)) && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Already Paid:</span>
                <span className="font-semibold text-green-600">{formatCurrency(totalPaidSoFar, currency)}</span>
              </div>
            )}
            {isAdd && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Remaining:</span>
                <span className="font-semibold text-amber-600">{formatCurrency(remainingBalance, currency)}</span>
              </div>
            )}
            {!isAdd && displayedRemaining > 0 && displayedRemaining < totalAmount && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Remaining:</span>
                <span className="font-semibold text-amber-600">{formatCurrency(displayedRemaining, currency)}</span>
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="paymentAmount">{isAdd ? "Amount to Add" : "Amount Received"}</Label>
            <Input
              id="paymentAmount"
              type="number"
              step="0.01"
              min="0"
              value={paymentAmount}
              onChange={(e) => handleAmountChange(e.target.value)}
              placeholder={isAdd ? `Up to ${formatCurrency(remainingBalance, currency)}` : undefined}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="paymentReference">Payment Reference</Label>
            <Input
              id="paymentReference"
              value={paymentReference}
              onChange={(e) => setPaymentReference(e.target.value)}
              placeholder="e.g. bank transfer ref, cheque number"
            />
          </div>

          {!isAdd && (
            <div className="space-y-2">
              <Label htmlFor="paymentStatus">Payment Status</Label>
              <Select value={paymentStatus} onValueChange={setPaymentStatus}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="unpaid">Unpaid</SelectItem>
                  <SelectItem value="partial">Part Paid</SelectItem>
                  <SelectItem value="paid">Paid</SelectItem>
                </SelectContent>
              </Select>
              {paymentStatus === "paid" && (
                <p className="text-xs text-muted-foreground">
                  Setting to "Paid" will move the lead to Executing status.
                </p>
              )}
            </div>
          )}

          {isAdd && (
            <div className="space-y-2">
              <Label htmlFor="paymentNotes">Notes (optional)</Label>
              <Textarea
                id="paymentNotes"
                value={paymentNotes}
                onChange={(e) => setPaymentNotes(e.target.value)}
                placeholder="e.g. deposit, second installment..."
                rows={2}
              />
            </div>
          )}

          {isRecord && (
            <div className="space-y-2">
              <Label htmlFor="paymentNotes">Notes (optional)</Label>
              <Textarea
                id="paymentNotes"
                value={paymentNotes}
                onChange={(e) => setPaymentNotes(e.target.value)}
                placeholder="e.g. deposit, full payment..."
                rows={2}
              />
            </div>
          )}

          {isEdit && (
            <div className="space-y-2">
              <Label htmlFor="correctionReason">Reason for Correction *</Label>
              <Textarea
                id="correctionReason"
                value={correctionReason}
                onChange={(e) => setCorrectionReason(e.target.value)}
                placeholder="Explain why this payment is being amended..."
                rows={3}
              />
            </div>
          )}

          {/* Payment history */}
          {existingPayments && existingPayments.length > 0 && (
            <div className="space-y-2">
              <Label className="text-muted-foreground">Payment History</Label>
              <ScrollArea className="max-h-[150px]">
                <div className="space-y-2">
                  {existingPayments.map((p) => (
                    <div key={p.id} className="flex justify-between items-center text-sm border rounded p-2">
                      <div>
                        <span className="font-medium">{formatCurrency(Number(p.amount), currency)}</span>
                        {p.payment_reference && (
                          <span className="text-muted-foreground ml-2">Ref: {p.payment_reference}</span>
                        )}
                        {p.notes && (
                          <span className="text-muted-foreground ml-2">— {p.notes}</span>
                        )}
                      </div>
                      <span className="text-xs text-muted-foreground">
                        {format(new Date(p.payment_date), "MMM d, yyyy")}
                      </span>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={() => mutation.mutate()}
            disabled={!canSubmit || mutation.isPending}
            className="bg-green-600 hover:bg-green-700"
          >
            {mutation.isPending ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : isEdit ? (
              "Save Correction"
            ) : isAdd ? (
              "Add Payment"
            ) : (
              "Record Payment"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
